import { useState, useEffect } from "react";
import { Clock, Play, Pause, StopCircle, UserPlus, UserMinus, UserX, UserCheck } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "../../../components/ui/card";
import { Button } from "../../../components/ui/button";
import { Badge } from "../../../components/ui/badge";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "../../../components/ui/dialog";
import { enqueueSnackbar } from "notistack";
import moment from "moment";
import { useAuth } from "../../../contexts/AuthContext";
import {
  filasTreinamentoService,
  filasResponsaveisService,
  FilaTreinamento,
  FilaResponsavel,
} from "../../../services/filasTreinamento";
import { SkeletonResponsavel, SkeletonListItem, StaggerWrapper, FadeInWrapper } from "../../../components/Skeleton";

export function FilasTreinamento() {
  const { militar } = useAuth();
  const [responsavel, setResponsavel] = useState<FilaResponsavel | null>(null);
  const [usuariosFila, setUsuariosFila] = useState<FilaTreinamento[]>([]);
  const [loading, setLoading] = useState(false);
  const [loadingResponsavel, setLoadingResponsavel] = useState(true);
  const [loadingFila, setLoadingFila] = useState(true);
  const [tempoResponsavel, setTempoResponsavel] = useState(0);
  const [temposFila, setTemposFila] = useState<Record<string, string>>({});

  // Modal retirar
  const [modalRetirarAberta, setModalRetirarAberta] = useState(false);
  const [usuarioSelecionado, setUsuarioSelecionado] = useState<FilaTreinamento | null>(null);

  // Modais de confirmação
  const [modalPausarAberta, setModalPausarAberta] = useState(false);
  const [modalFinalizarAberta, setModalFinalizarAberta] = useState(false);

  useEffect(() => {
    loadResponsavel();
    loadUsuariosFila();

    // Atualização automática a cada 15 segundos
    const autoUpdateInterval = setInterval(() => {
      loadResponsavel();
      loadUsuariosFila();
    }, 15000);

    return () => clearInterval(autoUpdateInterval);
  }, []);

  useEffect(() => {
    const interval = setInterval(() => {
      if (responsavel && !responsavel.data_hora_termino) {
        calcularTempoResponsavel();
      }
      calcularTemposFila();
    }, 1000);

    return () => clearInterval(interval);
  }, [responsavel, usuariosFila]);

  const loadResponsavel = async () => {
    try {
      setLoadingResponsavel(true);
      const data = await filasResponsaveisService.getAtual();
      setResponsavel(data);
    } catch (error: any) {
      console.error("Erro ao carregar responsável:", error);
    } finally {
      setLoadingResponsavel(false);
    }
  };

  const loadUsuariosFila = async () => {
    try {
      setLoadingFila(true);
      const data = await filasTreinamentoService.listarAtivos();
      setUsuariosFila(data);
    } catch (error: any) {
      console.error("Erro ao carregar usuários da fila:", error);
    } finally {
      setLoadingFila(false);
    }
  };

  const calcularTempoResponsavel = () => {
    if (!responsavel) return;

    const inicio = moment(responsavel.data_hora_inicio);
    const agora = moment();
    let segundosDecorridos = agora.diff(inicio, "seconds");

    if (responsavel.status === "pausado" && responsavel.data_hora_pausa) {
      const pausa = moment(responsavel.data_hora_pausa);
      segundosDecorridos = pausa.diff(inicio, "seconds");
    }

    segundosDecorridos -= responsavel.tempo_pausado_segundos;
    setTempoResponsavel(Math.max(0, segundosDecorridos));
  };

  const calcularTemposFila = () => {
    const novosTempos: Record<string, string> = {};
    usuariosFila.forEach((usuario) => {
      const entrada = moment(usuario.data_hora_entrada);
      const agora = moment();
      const segundos = agora.diff(entrada, "seconds");
      novosTempos[usuario.id] = formatarTempo(segundos);
    });
    setTemposFila(novosTempos);
  };

  const formatarTempo = (segundos: number) => {
    const horas = Math.floor(segundos / 3600);
    const minutos = Math.floor((segundos % 3600) / 60);
    const segs = segundos % 60;
    return `${String(horas).padStart(2, "0")}:${String(minutos).padStart(2, "0")}:${String(segs).padStart(2, "0")}`;
  };

  const handleIniciar = async () => {
    setLoading(true);
    try {
      const novoResponsavel = await filasResponsaveisService.iniciar();
      setResponsavel(novoResponsavel);
      enqueueSnackbar("Responsabilidade iniciada!", { variant: "success" });
    } catch (error: any) {
      enqueueSnackbar(error.response?.data?.message || "Erro ao iniciar", { variant: "error" });
    } finally {
      setLoading(false);
    }
  };

  const handlePegar = async () => {
    setLoading(true);
    try {
      const novoResponsavel = await filasResponsaveisService.pegar();
      setResponsavel(novoResponsavel);
      enqueueSnackbar("Responsabilidade assumida!", { variant: "success" });
    } catch (error: any) {
      enqueueSnackbar(error.response?.data?.message || "Erro ao pegar", { variant: "error" });
    } finally {
      setLoading(false);
    }
  };

  const handlePausar = async () => {
    setLoading(true);
    try {
      const responsavelAtualizado = await filasResponsaveisService.pausar();
      setResponsavel(responsavelAtualizado);
      enqueueSnackbar("Responsabilidade pausada", { variant: "info" });
    } catch (error: any) {
      enqueueSnackbar(error.response?.data?.message || "Erro ao pausar", { variant: "error" });
    } finally {
      setLoading(false);
    }
  };

  const handleRetomar = async () => {
    setLoading(true);
    try {
      const responsavelAtualizado = await filasResponsaveisService.retomar();
      setResponsavel(responsavelAtualizado);
      enqueueSnackbar("Responsabilidade retomada", { variant: "success" });
    } catch (error: any) {
      enqueueSnackbar(error.response?.data?.message || "Erro ao retomar", { variant: "error" });
    } finally {
      setLoading(false);
    }
  };

  const handleFinalizar = async () => {
    setLoading(true);
    try {
      await filasResponsaveisService.finalizar();
      setResponsavel(null);
      enqueueSnackbar("Responsabilidade finalizada!", { variant: "success" });
    } catch (error: any) {
      enqueueSnackbar(error.response?.data?.message || "Erro ao finalizar", { variant: "error" });
    } finally {
      setLoading(false);
    }
  };

  const handleEntrarFila = async () => {
    setLoading(true);
    try {
      await filasTreinamentoService.entrar();
      enqueueSnackbar("Você entrou na fila com sucesso!", { variant: "success" });
      loadUsuariosFila();
    } catch (error: any) {
      enqueueSnackbar(error.response?.data?.message || "Erro ao entrar na fila", { variant: "error" });
    } finally {
      setLoading(false);
    }
  };

  const handleMarcarIndisponivel = async (id: string) => {
    setLoading(true);
    try {
      await filasTreinamentoService.marcarIndisponivel(id);
      enqueueSnackbar("Usuário marcado como indisponível", { variant: "info" });
      loadUsuariosFila();
    } catch (error: any) {
      enqueueSnackbar(error.response?.data?.message || "Erro ao marcar", { variant: "error" });
    } finally {
      setLoading(false);
    }
  };

  const handleMarcarDisponivel = async (id: string) => {
    setLoading(true);
    try {
      await filasTreinamentoService.marcarDisponivel(id);
      enqueueSnackbar("Usuário marcado como disponível", { variant: "success" });
      loadUsuariosFila();
    } catch (error: any) {
      enqueueSnackbar(error.response?.data?.message || "Erro ao marcar", { variant: "error" });
    } finally {
      setLoading(false);
    }
  };

  const handleRetirar = async (motivo: "treinamento_enviado" | "offline_ausente") => {
    if (!usuarioSelecionado) return;

    setLoading(true);
    try {
      await filasTreinamentoService.retirar(usuarioSelecionado.id, motivo);
      enqueueSnackbar("Usuário retirado da fila", { variant: "success" });
      setModalRetirarAberta(false);
      setUsuarioSelecionado(null);
      loadUsuariosFila();
    } catch (error: any) {
      enqueueSnackbar(error.response?.data?.message || "Erro ao retirar", { variant: "error" });
    } finally {
      setLoading(false);
    }
  };

  const abrirModalRetirar = (usuario: FilaTreinamento) => {
    setUsuarioSelecionado(usuario);
    setModalRetirarAberta(true);
  };

  const souResponsavel = responsavel?.id_militar === militar?.id;
  const temResponsavel = !!responsavel && !responsavel.data_hora_termino;

  return (
    <div className="space-y-4">
      {/* Card de Responsabilidade */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Clock className="h-5 w-5" />
            Responsável pela Fila
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {loadingResponsavel ? (
            <SkeletonResponsavel />
          ) : temResponsavel ? (
            <FadeInWrapper>
              <div className="flex items-start gap-4">
                <div className="flex-shrink-0">
                  <img
                    src={`https://www.habbo.com.br/habbo-imaging/avatarimage?&user=${responsavel.militar.nick}&action=std&direction=2&head_direction=2&img_format=png&gesture=std&headonly=0&size=b`}
                    alt={responsavel.militar.nick}
                    className="w-auto h-32 object-contain"
                    style={{ imageRendering: 'pixelated' }}
                  />
                </div>
                <div className="flex-1">
                  <div className="flex items-start justify-between">
                    <div>
                      <p className="font-medium">
                        {responsavel.militar.patente?.abreviacao} {responsavel.militar.nick}
                      </p>
                      <p className="text-sm text-muted-foreground">
                        Desde {moment(responsavel.data_hora_inicio).format("DD/MM/YYYY HH:mm")}
                      </p>
                      {tempoResponsavel > 0 && (
                        <p className="text-sm font-mono text-blue-600 dark:text-blue-400 flex items-center gap-1 mt-1">
                          <Clock className="h-3 w-3" />
                          {formatarTempo(tempoResponsavel)}
                        </p>
                      )}
                    </div>
                    <Badge variant={responsavel.status === "pausado" ? "secondary" : "default"}>
                      {responsavel.status === "ativo" && "Ativo"}
                      {responsavel.status === "pausado" && "Pausado"}
                      {responsavel.status === "retomou" && "Ativo"}
                    </Badge>
                  </div>
                </div>
              </div>

              {souResponsavel && (
                <div className="flex gap-2 flex-wrap">
                  {responsavel.status === "pausado" ? (
                    <Button size="sm" onClick={handleRetomar} disabled={loading}>
                      <Play className="h-4 w-4 mr-2" />
                      Retomar
                    </Button>
                  ) : (
                    <Button size="sm" variant="outline" onClick={() => setModalPausarAberta(true)} disabled={loading}>
                      <Pause className="h-4 w-4 mr-2" />
                      Pausar
                    </Button>
                  )}
                  <Button size="sm" variant="destructive" onClick={() => setModalFinalizarAberta(true)} disabled={loading}>
                    <StopCircle className="h-4 w-4 mr-2" />
                    Finalizar
                  </Button>
                </div>
              )}

              {!souResponsavel && (
                <Button size="sm" onClick={handlePegar} disabled={loading}>
                  Pegar Responsabilidade
                </Button>
              )}
            </FadeInWrapper>
          ) : (
            <Button onClick={handleIniciar} disabled={loading}>
              <Play className="h-4 w-4 mr-2" />
              Iniciar Filas
            </Button>
          )}
        </CardContent>
      </Card>

      {/* Card de Usuários na Fila */}
      <Card>
        <CardHeader>
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
            <CardTitle className="flex flex-wrap items-center gap-2">
              <UserPlus className="h-5 w-5" />
              Fila de Treinamento
              <Badge variant="secondary" className="ml-2">
                {usuariosFila.filter((u) => u.status === "ativo").length} usuário(s)
              </Badge>
            </CardTitle>
            <Button onClick={handleEntrarFila} disabled={loading || !temResponsavel} className="w-full sm:w-auto">
              <UserPlus className="h-4 w-4 mr-2" />
              Entrar na Fila
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            {loadingFila ? (
              <>
                <SkeletonListItem />
                <SkeletonListItem />
                <SkeletonListItem />
              </>
            ) : usuariosFila.length === 0 ? (
              <p className="text-center text-muted-foreground py-8">Nenhum usuário na fila</p>
            ) : (
              usuariosFila.map((usuario, index) => (
                <StaggerWrapper key={usuario.id} index={index}>
                  <div
                    className={`p-4 border rounded-lg ${
                      usuario.status === "indisponivel"
                        ? "bg-yellow-50 dark:bg-yellow-900/20 border-yellow-300"
                        : "bg-gray-50 dark:bg-gray-800/50"
                    }`}
                  >
                  <div className="flex items-start gap-3">
                    <div className="flex-shrink-0">
                      <img
                        src={`https://www.habbo.com.br/habbo-imaging/avatarimage?&user=${usuario.militar.nick}&action=std&direction=2&head_direction=2&img_format=png&gesture=std&headonly=1&size=b`}
                        alt={usuario.militar.nick}
                        className="w-12 h-12 object-contain rounded-full"
                        style={{ imageRendering: 'pixelated' }}
                      />
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <p className="font-medium text-gray-900 dark:text-gray-100">
                          {usuario.militar.patente?.abreviacao} {usuario.militar.nick}
                        </p>
                        <Badge variant={usuario.status === "ativo" ? "default" : "secondary"}>
                          {usuario.status === "ativo" ? "Disponível" : "Indisponível"}
                        </Badge>
                      </div>
                      <p className="text-xs text-gray-500 dark:text-gray-400">
                        Entrou: {moment(usuario.data_hora_entrada).format("DD/MM/YYYY HH:mm")}
                      </p>
                      {temposFila[usuario.id] && (
                        <p className="text-sm font-mono text-blue-600 dark:text-blue-400 flex items-center gap-1 mt-2">
                          <Clock className="h-3 w-3" />
                          Na fila há: {temposFila[usuario.id]}
                        </p>
                      )}
                    </div>

                    {souResponsavel && (
                      <div className="flex gap-2 flex-shrink-0">
                        {usuario.status === "ativo" ? (
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => handleMarcarIndisponivel(usuario.id)}
                            disabled={loading}
                          >
                            <UserX className="h-4 w-4" />
                          </Button>
                        ) : (
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => handleMarcarDisponivel(usuario.id)}
                            disabled={loading}
                          >
                            <UserCheck className="h-4 w-4" />
                          </Button>
                        )}
                        <Button
                          size="sm"
                          variant="destructive"
                          onClick={() => abrirModalRetirar(usuario)}
                          disabled={loading}
                        >
                          <UserMinus className="h-4 w-4" />
                        </Button>
                      </div>
                    )}
                  </div>
                </div>
              </StaggerWrapper>
              ))
            )}
          </div>
        </CardContent>
      </Card>

      {/* Modal Confirmar Pausar */}
      <Dialog open={modalPausarAberta} onOpenChange={setModalPausarAberta}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Pausar Responsabilidade</DialogTitle>
            <DialogDescription>
              Tem certeza que deseja pausar a responsabilidade da fila?
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setModalPausarAberta(false)}>
              Cancelar
            </Button>
            <Button onClick={() => { setModalPausarAberta(false); handlePausar(); }} disabled={loading}>
              Confirmar Pausa
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Modal Confirmar Finalizar */}
      <Dialog open={modalFinalizarAberta} onOpenChange={setModalFinalizarAberta}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Finalizar Responsabilidade</DialogTitle>
            <DialogDescription>
              Tem certeza que deseja finalizar a responsabilidade da fila? Esta ação não pode ser desfeita.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setModalFinalizarAberta(false)}>
              Cancelar
            </Button>
            <Button variant="destructive" onClick={() => { setModalFinalizarAberta(false); handleFinalizar(); }} disabled={loading}>
              Finalizar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Modal Retirar da Fila */}
      <Dialog open={modalRetirarAberta} onOpenChange={setModalRetirarAberta}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Retirar da Fila</DialogTitle>
            <DialogDescription>
              Selecione o motivo para retirar <strong>{usuarioSelecionado?.militar?.nick}</strong> da fila.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2 py-4">
            <Button
              className="w-full justify-start"
              variant="outline"
              onClick={() => handleRetirar("treinamento_enviado")}
              disabled={loading}
            >
              Treinamento Enviado
            </Button>
            <Button
              className="w-full justify-start"
              variant="outline"
              onClick={() => handleRetirar("offline_ausente")}
              disabled={loading}
            >
              Offline/Ausente
            </Button>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setModalRetirarAberta(false)}>
              Cancelar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
